/**
 * One segment rule: a field, an operator limited to that field's `ops`, and a
 * value editor chosen by the field's `type` and the operator.
 *
 * ⚠️ The value is `unknown` on the wire and its SHAPE follows the operator:
 * `in` / `not_in` carry an array, the `is_*` operators carry nothing (`null`),
 * `within_last_days` a number, everything else a string. Changing the operator
 * therefore resets the value — carrying an array into a `contains` would be a
 * 422 naming a value the reader cannot see the reason for.
 */
import { useId, useMemo } from 'react';
import {
  Button,
  Combobox,
  Input,
  MultiCombobox,
  Select,
  type ComboboxOption,
  type MultiComboboxOption,
} from '@/components/ui';
import type { SegmentField, SegmentOp, SegmentRule } from '@/lib/types';
import { SEGMENT_OP_LABEL } from './segment-summary';
import { useRefOptions, isKnownRef, defaultRuleFor, defaultValueFor, LIST_OPS, NO_VALUE_OPS } from './ref-options';

/**
 * `SegmentField.options` normalised to `{value, label}`. The catalogue may
 * carry plain strings or objects; both are accepted so a shape change at the
 * server does not blank every enum picker.
 */
function enumOptions(field: SegmentField): MultiComboboxOption[] {
  const raw = (field as { options?: unknown }).options;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): MultiComboboxOption[] => {
    if (typeof entry === 'string') return [{ value: entry, label: entry }];
    if (entry && typeof entry === 'object') {
      const o = entry as { value?: unknown; label?: unknown };
      if (o.value === undefined || o.value === null) return [];
      return [{ value: String(o.value), label: typeof o.label === 'string' ? o.label : String(o.value) }];
    }
    return [];
  });
}

function asStrings(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value === null || value === undefined || value === '') return [];
  return [String(value)];
}

function toWire(values: string[], numeric: boolean): unknown[] {
  if (!numeric) return values;
  return values.map((v) => {
    const n = Number(v);
    return Number.isInteger(n) ? n : v;
  });
}

/**
 * Options plus any stored value absent from them, so a saved rule naming a row
 * that has since gone still renders as something rather than as a blank chip.
 */
function withStored(options: MultiComboboxOption[], stored: string[], loaded: boolean): MultiComboboxOption[] {
  if (!loaded) return options;
  const known = new Set(options.map((o) => o.value));
  const missing = stored.filter((v) => !known.has(v));
  if (missing.length === 0) return options;
  return [...options, ...missing.map((v) => ({ value: v, label: `#${v}`, hint: 'Not found' }))];
}

export interface RuleEditorProps {
  rule: SegmentRule;
  fields: SegmentField[];
  onChange(rule: SegmentRule): void;
  onRemove(): void;
  disabled?: boolean;
  /** Errors for this rule, keyed `field` / `op` / `value` / `''`. */
  errors?: { field?: string; op?: string; value?: string; rule?: string };
}

export function RuleEditor({ rule, fields, onChange, onRemove, disabled = false, errors }: RuleEditorProps) {
  const baseId = useId();
  const field = fields.find((f) => f.key === rule.field) ?? null;
  const op = rule.op;

  /*
   * The field picker is searchable. Each option carries its GROUP as the
   * `hint`, which Combobox searches together with the label — so typing either
   * the field name or the group finds it. Fields stay contiguous by group, in
   * the catalogue's own order, so the list still reads grouped when unfiltered.
   *
   * A stored field the catalogue no longer publishes is PINNED as an option,
   * labelled, rather than dropped: a Combobox falls back to its placeholder for
   * a value absent from its options, which would read "Choose a field" over a
   * rule that really names one — and the next edit would silently replace it.
   */
  const fieldOptions = useMemo<ComboboxOption[]>(() => {
    const groups = new Map<string, SegmentField[]>();
    for (const f of fields) {
      const list = groups.get(f.group) ?? [];
      list.push(f);
      groups.set(f.group, list);
    }
    const options: ComboboxOption[] = [...groups.entries()].flatMap(([group, list]) =>
      list.map((f) => ({ value: f.key, label: f.label, hint: group })),
    );
    if (rule.field && !fields.some((f) => f.key === rule.field)) {
      options.unshift({ value: rule.field, label: `Unknown field ${rule.field}`, hint: 'No longer offered' });
    }
    return options;
  }, [fields, rule.field]);

  const changeField = (key: string) => {
    const next = fields.find((f) => f.key === key);
    if (next) onChange(defaultRuleFor(next));
  };

  const changeOp = (nextOp: SegmentOp) => {
    // Keep a list value when moving between the two list operators — the
    // shape is the same and the reader's selection is not something to lose.
    const keep = LIST_OPS.has(nextOp) && LIST_OPS.has(op);
    onChange({ ...rule, op: nextOp, value: keep ? rule.value : defaultValueFor(nextOp) });
  };

  const anyError = errors?.field ?? errors?.op ?? errors?.value ?? errors?.rule;

  return (
    <div className="grid min-w-0 gap-1.5">
      {/*
        The Remove button has its OWN `auto` track from `sm`, and every other
        track can shrink to zero (`minmax(0, …)` plus `min-w-0` on each item),
        so a long field label or a wide value editor can never push it out of
        view. Below `sm` the grid is one column and it sits on its own line,
        right-aligned.

        From `sm` the four tracks read as one sentence — [Field] [condition]
        [value] [Remove] — and below it they stack one per line, which is the
        clean wrap at 320px.
      */}
      <div className="grid gap-2 sm:grid-cols-[minmax(0,15rem)_minmax(0,11rem)_minmax(0,1fr)_auto] sm:items-start">
        <div className="min-w-0">
          {/*
            Combobox's trigger takes no `aria-label`, and a `<label for>` would
            REPLACE the selected value as the button's name. A described-by
            span keeps the value as the name and still says what the control is.
          */}
          <span id={`${baseId}-field-role`} className="sr-only">
            Rule field
          </span>
          <Combobox
            id={`${baseId}-field`}
            aria-describedby={`${baseId}-field-role`}
            value={rule.field ?? ''}
            onChange={changeField}
            options={fieldOptions}
            placeholder="Choose a field"
            searchPlaceholder="Search fields…"
            disabled={disabled}
            invalid={Boolean(errors?.field)}
          />
        </div>

        <div className="min-w-0">
          <Select
            aria-label="Condition"
            value={op}
            onChange={(e) => changeOp(e.target.value as SegmentOp)}
            disabled={disabled || !field}
            invalid={Boolean(errors?.op)}
          >
            {(field ? (field.ops as SegmentOp[]) : [op]).map((o) => (
              <option key={o} value={o}>
                {SEGMENT_OP_LABEL[o] ?? o}
              </option>
            ))}
            {field && !(field.ops as SegmentOp[]).includes(op) && <option value={op}>{SEGMENT_OP_LABEL[op] ?? op}</option>}
          </Select>
        </div>

        <div className="min-w-0">
          {field && (
            <ValueEditor
              id={`${baseId}-value`}
              field={field}
              op={op}
              value={rule.value}
              onChange={(value) => onChange({ ...rule, value })}
              disabled={disabled}
              invalid={Boolean(errors?.value)}
            />
          )}
        </div>

        <Button
          variant="secondary"
          size="sm"
          aria-label="Remove rule"
          className="justify-self-end sm:justify-self-auto"
          onClick={onRemove}
          disabled={disabled}
        >
          Remove
        </Button>
      </div>
      {anyError && <p className="m-0 text-xs text-danger">{anyError}</p>}
    </div>
  );
}

interface ValueEditorProps {
  id: string;
  field: SegmentField;
  op: SegmentOp;
  value: unknown;
  onChange(value: unknown): void;
  disabled: boolean;
  invalid: boolean;
}

function ValueEditor({ id, field, op, value, onChange, disabled, invalid }: ValueEditorProps) {
  if (NO_VALUE_OPS.has(op)) return null;

  if (op === 'within_last_days') {
    return (
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={1}
        aria-label="Number of days"
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? null : Number(raw));
        }}
        disabled={disabled}
        invalid={invalid}
      />
    );
  }

  if (field.type === 'ref' || field.type === 'enum') {
    return (
      <OptionValueEditor
        id={id}
        field={field}
        op={op}
        value={value}
        onChange={onChange}
        disabled={disabled}
        invalid={invalid}
      />
    );
  }

  if (field.type === 'date') {
    return (
      <Input
        id={id}
        type="date"
        aria-label="Date"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        invalid={invalid}
      />
    );
  }

  return (
    <Input
      id={id}
      aria-label="Value"
      value={typeof value === 'string' ? value : value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      invalid={invalid}
    />
  );
}

function OptionValueEditor({ id, field, op, value, onChange, disabled, invalid }: ValueEditorProps) {
  const ref = field.type === 'ref' ? (field as { ref?: string | null }).ref ?? null : null;
  const remote = useRefOptions(ref);
  const isEnum = field.type === 'enum';

  const loaded = isEnum || remote.data !== null;
  const numeric = !isEnum && (remote.data?.numeric ?? false);
  const stored = asStrings(value);
  const options = withStored(isEnum ? enumOptions(field) : (remote.data?.options ?? []), stored, loaded);

  if (!isEnum && (remote.failed || !isKnownRef(ref))) {
    return (
      <p className="m-0 flex h-9 items-center text-xs text-danger max-sm:h-11">
        {isKnownRef(ref) ? 'The options for this field could not be loaded.' : 'This field has no option list.'}
      </p>
    );
  }

  if (LIST_OPS.has(op)) {
    return (
      <MultiCombobox
        id={id}
        values={stored}
        onChange={(next) => onChange(toWire(next, numeric))}
        options={options}
        loading={!loaded}
        disabled={disabled}
        invalid={invalid}
        placeholder="Choose values…"
        selectAll
      />
    );
  }

  return (
    <Combobox
      id={id}
      value={stored[0] ?? ''}
      onChange={(next) => {
        if (next === '') return onChange('');
        onChange(numeric ? (toWire([next], true)[0] ?? next) : next);
      }}
      options={options}
      loading={!loaded}
      disabled={disabled}
      invalid={invalid}
      placeholder="Choose a value…"
    />
  );
}
